import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
} from 'recharts';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import {
  COLOR_BLUE_BLACK_LIGHT,
  COLOR_PRIMARY,
  exchangeRates,
  OVERVIEW_DASHBOARD_FILTER_DEMO_DATA,
  shortMonthNames,
} from 'src/utils/const';
import { KPI_DEFINITIONS } from 'src/utils/kpiDefinitions';
import {
  abbreviateNumber,
  convertCurrency,
  formatCurrency,
  trackConfigurableOverviewGraphActivation,
} from 'src/utils/functions';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import {
  COLOR_GREY_400,
  COLOR_GREY_600,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_1_600,
  COLOR_SECONDARY_2_600,
  COLOR_SECONDARY_2_700,
  COLOR_TERTIARY_1_600,
} from 'src/utils/colors';
import { FilteredListingsDashboardDataType } from 'src/interfaces/idashboard';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import { logAmplitudeEvent } from 'src/utils/analytics';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';

const FilterChip = ({
  title,
  isActive,
  color,
  handleOnClick,
}: {
  title: string;
  isActive: boolean;
  color?: string;
  handleOnClick?: () => void;
}) => {
  color = !isActive ? '#999' : color ? color : COLOR_BLUE_BLACK_LIGHT;

  const style = {
    chip: {
      backgroundColor: isActive ? color : '#fff',
      borderColor: color,
      color: isActive ? '#fff' : color,
    },
    icon: {
      color: isActive ? '#fff' : color,
    },
  };

  return (
    <>
      <Chip
        label={title}
        variant={isActive ? 'filled' : 'outlined'}
        size="small"
        deleteIcon={
          isActive ? (
            <RemoveCircleOutlineRoundedIcon style={style.icon} />
          ) : (
            <AddCircleOutlineRoundedIcon style={style.icon} />
          )
        }
        onClick={() => handleOnClick && handleOnClick()}
        onDelete={() => handleOnClick && handleOnClick()}
        sx={style.chip}
      />
    </>
  );
};

type CatagoryMapType = { [key: string]: { name: string } };
type OptionalCatagoryMapType = { [key: string]: { name: string; isActive: boolean } };

const processDynamicRangeCatagoryMap = (
  rangeObject: any,
  currency: string,
  isCurrency?: boolean
) => {
  if (!rangeObject) return {};

  const resultObject: CatagoryMapType = {};
  for (const key in rangeObject) {
    if (rangeObject.hasOwnProperty(key)) {
      resultObject[key] = {
        name: !isCurrency
          ? key
          : key
              .split('-')
              .map(
                data =>
                  (exchangeRates[currency]?.symbol || '') +
                  abbreviateNumber(convertCurrency(currency, Number(data)), true)
              )
              .join(' - '),
      };
    }
  }
  return resultObject;
};

const PremiumOverlay = ({
  isXAxisTickVertical,
  handleClick,
  offsetX,
  offsetY,
  offsetWidth,
  offsetHeight,
}: {
  isXAxisTickVertical: boolean | undefined;
  handleClick: any;
  offsetX?: number;
  offsetY?: number;
  offsetWidth?: number;
  offsetHeight?: number;
}) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: offsetY ? offsetY + 'px' : 0,
        left: offsetX ? offsetX + 'px' : '60px',
        width: `calc(100% - 30px + ${offsetWidth ? offsetWidth + 'px' : '0px'})`,
        height: `calc(${isXAxisTickVertical ? '70%' : '80%'} + ${
          offsetHeight ? offsetHeight + 'px' : '0px'
        })`,
        background: '#fffa',
        backgroundBlendMode: 'multiply',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="body1"
        onClick={() => handleClick('expert')}
        sx={{
          fontSize: '20px',
          fontWeight: 500,
          color: COLOR_PRIMARY_2,
          cursor: 'pointer',
          pointerEvents: 'auto',
          textDecoration: 'underline',
        }}
      >
        {useUpgradeMesageProFeature()}
      </Typography>
    </Box>
  );
};

const processCurrencyGraphLabelRange = (label: string, currency: string) => {
  if (!label || !currency) return '';

  return label
    .split('-')
    .map(
      valueUSD =>
        (exchangeRates[currency]?.symbol || '') +
        abbreviateNumber(convertCurrency(currency, Number(valueUSD)))
    )
    .join('-');
};

const MainChartTooltip = props => {
  const { active, payload, title, metricNameMap, currency } = props;

  useEffect(() => {
    if (active) {
      logAmplitudeEvent('tooltip_interacted', {
        tooltip_type: 'graph',
        parent: `filter_bar_chart`,
      });
    }
  }, [active]);

  if (active && payload && payload.length > 0) {
    const _payload = payload[0].payload;
    const unit = payload[0].unit;
    const barColor = payload[0].color;

    const isCurrency = unit !== '%';

    return (
      <Card sx={{ boxShadow: `0px 0px 10px 0px ${COLOR_GREY_600}`, p: 0, m: 0 }}>
        <CardContent style={{ background: '#fff', padding: '10px', margin: 0 }}>
          <Typography variant="body1" style={{ fontWeight: 'bolder', color: COLOR_GREY_600 }}>
            {payload[0].payload.timePeriod}
          </Typography>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            <span style={{ color: COLOR_GREY_600 }}>{metricNameMap[title]}:</span>{' '}
            <span style={{ color: barColor }}>
              {isCurrency && unit}
              {isCurrency
                ? formatCurrency(convertCurrency(currency, _payload[title]).toFixed(0))
                : _payload[title]}
              {!isCurrency && unit}
            </span>
          </Typography>
        </CardContent>
      </Card>
    );
  }
  return null;
};

const ConfigurableFilterBarCard = ({
  filteredListingsData,
  cachedMonthlyLTMData,
  isLoading,
  currency,
  isDisabled,
  handleOnSampleClick,
  isCacheLoading,
  bestPropertyTypeGraphRef,
  configurableFilterBarCardSelectionModalRef,
  setSelectedGraphExportDetails,
  embedded = false,
}: {
  filteredListingsData: FilteredListingsDashboardDataType;
  cachedMonthlyLTMData: any;
  isLoading: boolean;
  currency: string;
  isDisabled: boolean;
  handleOnSampleClick: any;
  isCacheLoading: boolean;
  bestPropertyTypeGraphRef: any;
  configurableFilterBarCardSelectionModalRef: any;
  setSelectedGraphExportDetails: any;
  embedded?: boolean;
}) => {
  const cachedMarketData = useSelector(state => (state as any).dashboard.cachedMarketData);
  const [metricsByBedroom, setMetricsByBedroom] = useState<any>(null);
  const [shouldShowLoading, setShouldShowLoading] = useState<boolean>(true);

  // TODO: Add other filter types as we add more data to the cached market data
  const filterType = 'bedrooms';

  useEffect(() => {
    /*
      isDisabled = user not allowed to view the graph
    */
    if (isDisabled) {
      setShouldShowLoading(false);
      return;
    }

    if (filterType === 'bedrooms') {
      setShouldShowLoading(
        metricsByBedroom == OVERVIEW_DASHBOARD_FILTER_DEMO_DATA.propertySize ||
          metricsByBedroom == null
      );
    }
  }, [isDisabled, isLoading, metricsByBedroom, filterType]);

  useEffect(() => {
    if (isDisabled) {
      setMetricsByBedroom(filteredListingsData?.propertySize || {});
      return;
    }

    if (!cachedMarketData?.loading && cachedMarketData?.data) {
      const { adr_breakdown, occupancy_breakdown } = cachedMarketData.data;
      const adrByBedroom = adr_breakdown?.bedroom || {};
      const occupancyByBedroom = occupancy_breakdown?.bedroom || {};
      const revenueByBedroom = cachedMarketData.data.revenue_breakdown?.bedroom || {};

      if (adrByBedroom && occupancyByBedroom) {
        const updatedPropertySize = { ...OVERVIEW_DASHBOARD_FILTER_DEMO_DATA.propertySize };

        Object.keys(updatedPropertySize).forEach(bedroom => {
          const bedroomKey = bedroom === 'studio' ? '0' : bedroom === '6+' ? '6' : bedroom;
          updatedPropertySize[bedroom]['adr'] = adrByBedroom[bedroomKey] || null;
          updatedPropertySize[bedroom]['occupancyRate'] = occupancyByBedroom[bedroomKey] || null;
          updatedPropertySize[bedroom]['revenue'] = revenueByBedroom[bedroomKey] || null;
        });

        setMetricsByBedroom(updatedPropertySize);
      }
    }
  }, [cachedMarketData]);

  const graphConfig = {
    bedrooms: {
      data: metricsByBedroom,
      distributionData: filteredListingsData?.propertySizeMetricsDistribution,
      title: 'Bedrooms',
      tooltipText: 'See what is the best number of bedrooms for Airbnb in this market.',
      metricTabs: ['occupancyRate', 'adr', 'revenue'],
      catagoryMap: {
        studio: { name: 'Studio' },
        '1': { name: '1' },
        '2': { name: '2' },
        '3': { name: '3' },
        '4': { name: '4' },
        '5': { name: '5' },
        '6+': { name: '6+' },
      },
      optionalCatagoryMap: null,
      color: COLOR_SECONDARY_2_700,
      isXaxisCurrency: false,
      xAxisTitle: 'Bedrooms',
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: true,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: cachedMonthlyLTMData ? cachedMonthlyLTMData.bedroom : null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: 'Bedroom',
      titleHighlightType: 'prefix',
      xAxisOrder: ['studio', '1', '2', '3', '4', '5', '6+'],
      isXAxisTickVertical: false,
    },
    propertyType: {
      data: filteredListingsData?.propertyType,
      distributionData: filteredListingsData?.propertyTypeMetricsDistribution,
      title: 'Property Type',
      tooltipText: 'See what property type is most in demand in this market.',
      metricTabs: ['occupancyRate', 'adr', 'revenue'],
      catagoryMap: {
        entire_home: { name: 'Entire Home' },
        private_room: { name: 'Private Room' },
      },
      optionalCatagoryMap: {
        condo: { name: 'Apartment / Condo', isActive: true },
        hotel_room: { name: 'Hotel Room', isActive: true },
        shared_room: { name: 'Shared Room', isActive: true },
      },
      color: COLOR_PRIMARY,
      isXaxisCurrency: false,
      xAxisTitle: null,
      maxBarSize: 13,
      showSelectionChips: true,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: true,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: cachedMonthlyLTMData ? cachedMonthlyLTMData.property_type : null,
      isGraphLocked: false,
      tooltipTitleSuffix: null,
      titleHighlightType: 'prefix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
    bathrooms: {
      data: filteredListingsData?.bathroomNumber,
      distributionData: filteredListingsData?.bathroomNumberMetricsDistribution,
      title: 'Bathrooms',
      tooltipText: 'See how the number of bathrooms of a property impacts its performance.',
      metricTabs: ['occupancyRate'],
      catagoryMap: {
        studio: { name: 'Studio' },
        '1': { name: '1' },
        '2': { name: '2' },
        '3': { name: '3' },
        '4': { name: '4' },
        '5': { name: '5' },
        '6+': { name: '6+' },
      },
      optionalCatagoryMap: null,
      color: COLOR_SECONDARY_1_600,
      isXaxisCurrency: false,
      xAxisTitle: 'Bathrooms',
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: false,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: 'Bathrooms',
      titleHighlightType: 'suffix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
    adr: {
      data: filteredListingsData?.adrRange,
      distributionData: filteredListingsData?.adrRangeMetricsDitribution,
      title: 'Average Daily Rate',
      tooltipText: KPI_DEFINITIONS.ADR_listing_LTM.distributionImpact,
      metricTabs: ['occupancyRate'],
      catagoryMap: 'dynamicRange',
      optionalCatagoryMap: null,
      color: COLOR_SECONDARY_2_600,
      isXaxisCurrency: true,
      xAxisTitle: null,
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: false,
      dynamicTooltipCatagory: ['Budget', 'Economy', 'Midscale', 'Upscale', 'Luxury'],
      graphTooltipLTMData: null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: null,
      titleHighlightType: 'suffix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
    rating: {
      data: filteredListingsData?.ratingRange,
      distributionData: filteredListingsData?.ratingRangeMetricsDistribution,
      title: 'Ratings',
      tooltipText: 'See how the review rating of a property impacts its occupancy rate',
      metricTabs: ['occupancyRate'],
      catagoryMap: {
        '0-4': { name: 'below 4' },
        '4-4.3': { name: '4-4.3' },
        '4.3-4.6': { name: '4.3-4.6' },
        '4.6-4.8': { name: '4.6- 4.8' },
        '4.8-5.1': { name: '4.8-5' },
      },
      optionalCatagoryMap: null,
      color: COLOR_PRIMARY,
      isXaxisCurrency: false,
      xAxisTitle: 'ratings out of 5★',
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: false,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: '★ ratings',
      titleHighlightType: 'suffix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
    hostListingCount: {
      data: filteredListingsData?.listingsPerHost,
      distributionData: filteredListingsData?.listingsPerHostMetricsDistribution,
      title: 'Listings per host',
      tooltipText: 'See how the number of listings owned by a host impacts its performanmce',
      metricTabs: ['occupancyRate'],
      catagoryMap: {
        '1': { name: '1' },
        '2-5': { name: '2-5' },
        '6-20': { name: '6-20' },
        '21+': { name: '21+' },
      },
      optionalCatagoryMap: null,
      color: COLOR_PRIMARY,
      isXaxisCurrency: false,
      xAxisTitle: 'listings',
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: false,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: 'Listings',
      titleHighlightType: 'prefix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
    minimumNights: {
      data: filteredListingsData?.minNightRange,
      distributionData: filteredListingsData?.minNightRangeMetricsDistribution,
      title: 'Minimum Nights',
      tooltipText: 'See how the properties with different minimum nights impact their performance.',
      metricTabs: ['occupancyRate'],
      catagoryMap: {
        '1-6': { name: '1-6' },
        '7-29': { name: '7-29' },
        '30-999': { name: '30+' },
      },
      optionalCatagoryMap: null,
      color: COLOR_TERTIARY_1_600,
      isXaxisCurrency: false,
      xAxisTitle: 'nights',
      maxBarSize: 13,
      showSelectionChips: false,
      isGraphSizeSmall: true,
      showLTMGraphTooltip: false,
      dynamicTooltipCatagory: null,
      graphTooltipLTMData: null,
      isGraphLocked: isDisabled,
      tooltipTitleSuffix: 'Minimum nights',
      titleHighlightType: 'prefix',
      xAxisOrder: null,
      isXAxisTickVertical: false,
    },
  };

  const [tabSelected, setTabSelected] = useState<'occupancyRate' | 'adr' | 'revenue' | 'supply'>(
    'revenue'
  );

  const [catagoryMapState, setCatagoryMapState] = useState<CatagoryMapType>(
    (graphConfig[filterType].catagoryMap as any) === 'dynamicRange'
      ? {}
      : (graphConfig[filterType].catagoryMap as CatagoryMapType)
  );
  const [
    optionalCatagoryMapState,
    setOptionalCatagoryMapState,
  ] = useState<OptionalCatagoryMapType | null>(graphConfig[filterType].optionalCatagoryMap);

  useEffect(() => {
    if ((graphConfig[filterType].catagoryMap as any) === 'dynamicRange') {
      setCatagoryMapState(
        processDynamicRangeCatagoryMap(
          graphConfig[filterType].data,
          currency,
          graphConfig[filterType].isXaxisCurrency
        )
      );
    } else {
      setCatagoryMapState(graphConfig[filterType].catagoryMap as CatagoryMapType);
    }
    setOptionalCatagoryMapState(graphConfig[filterType].optionalCatagoryMap);

    if (graphConfig[filterType].metricTabs.length == 1) {
      setTabSelected('occupancyRate');
      configurableFilterBarCardSelectionModalRef.current = {
        ...configurableFilterBarCardSelectionModalRef.current,
        tabSelected: 'Occupancy Rate',
      };
    }
  }, [currency, filterType]);

  const processGraphData = (dataObj: any) => {
    if (!dataObj) return [];

    const graphResultArray: { [key: string]: string | number }[] = [];

    const dataToUse = graphConfig[filterType]?.xAxisOrder
      ? graphConfig[filterType].xAxisOrder
      : Object.keys(dataObj || {});

    dataToUse &&
      dataToUse.forEach(key => {
        if (catagoryMapState?.[key]) {
          graphResultArray.push({
            ...(dataObj[key] || {}),
            name: key,
          });
        }

        if (optionalCatagoryMapState?.[key]?.isActive) {
          graphResultArray.push({
            ...(dataObj[key] || {}),
            name: key,
            isOptionalKey: true,
          });
        }
      });

    return graphResultArray;
  };

  const handleFilterChipClick = (valueClicked: string) => {
    optionalCatagoryMapState &&
      setOptionalCatagoryMapState(prevState => {
        if (!prevState) return prevState;

        const prevStateClone = { ...prevState };
        prevStateClone[valueClicked].isActive = prevState[valueClicked].isActive ? false : true;
        return prevStateClone;
      });
  };

  const metricNameMap = {
    occupancyRate: 'Occupancy Rate',
    adr: 'Daily Rate',
    revenue: 'Revenue',
  };

  const isTabSelectedDataCurrency = tabSelected === 'adr' || tabSelected === 'revenue';

  const processDataString = (dateStr: string) => {
    const [year, month] = dateStr.split('-').map(Number);
    return shortMonthNames[month - 1] + ', ' + (year % 100);
  };

  const getFormattedDistGraphTick = tick => {
    const isCurrency = tabSelected != 'occupancyRate';
    let [start, end] = tick.split(' - ').map(Number);

    if (isCurrency) {
      return `${exchangeRates[currency].symbol}${abbreviateNumber(
        convertCurrency(currency, start)
      )}-${exchangeRates[currency].symbol}${abbreviateNumber(convertCurrency(currency, end))}`;
    } else {
      return `${start}-${end}%`;
    }
  };

  const ConfigSelect = ({
    value,
    setValue,
    optionsMap,
    customStyle,
    activeColor,
  }: {
    value: string;
    setValue: React.Dispatch<React.SetStateAction<any>>;
    optionsMap: { [index: string]: string };
    customStyle?: any;
    activeColor: string;
  }) => (
    <FormControl variant="standard" sx={{ m: 0, minWidth: 120 }}>
      <Select
        value={value}
        onChange={event => {
          logAmplitudeEvent('filter_bar_chart_configured');
          setValue(event.target.value);
          trackConfigurableOverviewGraphActivation();
        }}
        MenuProps={{
          autoFocus: false,
        }}
        sx={{
          fontWeight: 500,
          paddingTop: 0,
          '& #demo-simple-select-standard': {
            paddingTop: '0px !important',
            paddingLeft: '5px',
          },
          '& .MuiSelect-standard': {
            paddingTop: '0px !important',
            backgroundColor: 'transparent',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
            outline: 'none',
          },
          fieldset: {
            border: 'none !important',
            outline: 'none',
          },
          ...customStyle,
        }}
        renderValue={selected => (
          <Typography variant="body1" sx={{ fontWeight: 500, color: activeColor }}>
            {optionsMap[selected]}
          </Typography>
        )}
      >
        {Object.keys(optionsMap).map(option => (
          <MenuItem value={option}>{optionsMap[option]}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const theme = useTheme();

  const processedGraphData =
    filterType && graphConfig[filterType].data && processGraphData(graphConfig[filterType].data);

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      item
      xs={12}
      sx={{
        ...(embedded
          ? { border: '1px solid #E0E0E0', borderRadius: 1 }
          : { boxShadow: '0 4px 10px #0003', borderRadius: 1 }),
        ...theme.utils.cardPadding,
      }}
    >
      <Grid container item>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1" className="subHeadings">
              <ConfigSelect
                value={tabSelected}
                setValue={value => {
                  setTabSelected(value);
                  configurableFilterBarCardSelectionModalRef.current = {
                    ...configurableFilterBarCardSelectionModalRef.current,
                    tabSelected: metricNameMap[value],
                  };
                }}
                optionsMap={Object.keys(metricNameMap)
                  .filter(key => graphConfig[filterType].metricTabs.includes(key))
                  .reduce((obj, key) => {
                    obj[key] = metricNameMap[key];
                    return obj;
                  }, {})}
                activeColor={COLOR_PRIMARY_2}
              />{' '}
              by Bedrooms
              <InfoButton
                tooltipContent={
                  <Grid container direction="column" alignItems="flex-start">
                    <Typography variant="body2" style={{ fontWeight: 500, marginBottom: '5px' }}>
                      {graphConfig[filterType].tooltipText}
                    </Typography>
                    <Grid container item xs={12}>
                      <>
                        {catagoryMapState &&
                          (graphConfig[filterType].catagoryMap as any) === 'dynamicRange' &&
                          graphConfig[filterType].dynamicTooltipCatagory &&
                          Object.keys(catagoryMapState).map((catagoryMap, idx) => (
                            <Grid
                              item
                              xs={12}
                              key={idx}
                              direction={'row'}
                              sx={{ marginBottom: '2px' }}
                            >
                              <Typography variant="body2">
                                <span style={{ fontWeight: 'bold' }}>
                                  {graphConfig[filterType].dynamicTooltipCatagory![idx] + ': '}
                                </span>
                                {processCurrencyGraphLabelRange(catagoryMap, currency)}
                              </Typography>
                            </Grid>
                          ))}
                      </>
                    </Grid>
                  </Grid>
                }
              />
            </Typography>

            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                if (!processedGraphData || !processedGraphData.length) return;

                let exportableGraphData = processedGraphData.map((item: any) => [
                  item.name,
                  item[tabSelected],
                ]);
                exportableGraphData.unshift([
                  graphConfig[filterType].xAxisTitle,
                  metricNameMap[tabSelected],
                ]);

                exportableGraphData.length &&
                  setSelectedGraphExportDetails({
                    graphData: exportableGraphData,
                    fieldName: `revenue_by_bedroom.csv`,
                    graphType: 'configurableFilterBarCardGraphs',
                  });
              }}
            >
              <DownloadRoundedIcon />
            </IconButton>
          </Box>
        </Grid>

        {shouldShowLoading || isCacheLoading ? (
          <LoadingCard height={150} type="bars" />
        ) : (
          <>
            {graphConfig[filterType].showSelectionChips && (
              <Grid xs={12} item container>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  justifyContent="flex-start"
                  alignItems="center"
                  gap={'10px'}
                  sx={{ width: '100%', marginTop: '5px', marginBottom: '0px' }}
                >
                  {optionalCatagoryMapState &&
                    Object.keys(optionalCatagoryMapState).map(catagory => (
                      <FilterChip
                        title={optionalCatagoryMapState[catagory].name}
                        isActive={optionalCatagoryMapState[catagory].isActive}
                        color={graphConfig[filterType].color}
                        handleOnClick={() => handleFilterChipClick(catagory)}
                      />
                    ))}
                </Stack>
              </Grid>
            )}

            <Grid
              container
              item
              xs={12}
              sx={{ position: 'relative', paddingTop: '20px' }}
              id={'filter-graph-container' + graphConfig[filterType].title}
              ref={bestPropertyTypeGraphRef}
            >
              {processedGraphData && (
                <>
                  <ResponsiveContainer
                    width="100%"
                    height={graphConfig[filterType].isGraphSizeSmall ? 180 : 250}
                  >
                    <BarChart
                      data={processedGraphData}
                      maxBarSize={
                        graphConfig[filterType].maxBarSize ? graphConfig[filterType].maxBarSize : 20
                      }
                      margin={{
                        left: tabSelected === 'revenue' ? 6 : tabSelected === 'adr' ? 0 : -15,
                        bottom: graphConfig[filterType].isXAxisTickVertical ? 40 : 10,
                      }}
                      style={{
                        fontFamily: 'Roboto',
                        position: 'relative',
                      }}
                    >
                      {!graphConfig[filterType].isGraphSizeSmall ? (
                        <CartesianGrid strokeDasharray="3 3" />
                      ) : (
                        <CartesianGrid
                          vertical={false}
                          horizontal={true}
                          stroke={COLOR_GREY_400}
                          strokeDasharray="6 6"
                        />
                      )}
                      <XAxis
                        dataKey="name"
                        tickFormatter={props =>
                          optionalCatagoryMapState && optionalCatagoryMapState[props]
                            ? optionalCatagoryMapState[props].name
                            : catagoryMapState[props].name
                        }
                        tickLine={false}
                        axisLine={graphConfig[filterType].isGraphSizeSmall ? false : true}
                        angle={graphConfig[filterType].isXAxisTickVertical ? -90 : 0}
                        textAnchor={graphConfig[filterType].isXAxisTickVertical ? 'end' : 'middle'}
                        style={{
                          fontSize: graphConfig[filterType].isGraphSizeSmall ? '0.75rem' : '14px',
                          fontFamily: 'Roboto',
                        }}
                      >
                        {graphConfig[filterType].xAxisTitle && (
                          <Label
                            value={graphConfig[filterType].xAxisTitle}
                            offset={-5}
                            position="insideBottom"
                            style={{
                              fontSize: '0.75rem',
                              fontFamily: 'Roboto',
                            }}
                            fill={COLOR_GREY_600}
                          />
                        )}
                      </XAxis>
                      <YAxis
                        tickLine={false}
                        axisLine={graphConfig[filterType].isGraphSizeSmall ? false : true}
                        tickFormatter={props =>
                          (isTabSelectedDataCurrency ? exchangeRates[currency].symbol : '') +
                          abbreviateNumber(
                            isTabSelectedDataCurrency ? convertCurrency(currency, props) : props,
                            true
                          ) +
                          (tabSelected === 'occupancyRate' ? '%' : '')
                        }
                        style={{
                          fontSize: graphConfig[filterType].isGraphSizeSmall ? '0.75rem' : '14px',
                          fontFamily: 'Roboto',
                        }}
                      />
                      <Bar
                        dataKey={tabSelected}
                        fill={COLOR_PRIMARY}
                        unit={
                          tabSelected === 'occupancyRate'
                            ? '%'
                            : tabSelected === 'supply'
                            ? 'listings'
                            : exchangeRates[currency].symbol
                        }
                        style={{ cursor: 'pointer' }}
                        radius={[100, 100, 100, 100]}
                      />
                      <Tooltip
                        cursor={{ fill: 'transparent' }}
                        content={
                          <MainChartTooltip
                            title={tabSelected}
                            metricNameMap={metricNameMap}
                            currency={currency}
                          />
                        }
                        position={{ y: -50 }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                  {graphConfig[filterType].isGraphLocked && (
                    <PremiumOverlay
                      isXAxisTickVertical={graphConfig[filterType].isXAxisTickVertical}
                      handleClick={handleOnSampleClick}
                    />
                  )}
                </>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default React.memo(ConfigurableFilterBarCard);
